import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Voice briefing hook with two engines:
 *   1. Server-side Camb.ai TTS (preferred) — high-quality MARS voice. The
 *      server fetches weather, builds a rich actionable briefing, and streams
 *      audio directly into an HTMLAudioElement here.
 *   2. Browser Web Speech API (fallback) — used when Camb is unavailable
 *      (network failure, server error, missing API key, etc).
 */
export function useVoiceBriefing() {
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [engine, setEngine] = useState<"camb" | "browser" | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null);

  // Detect browser TTS support and load voices.
  useEffect(() => {
    const audioOk = typeof Audio !== "undefined";
    const speechOk = typeof window !== "undefined" && "speechSynthesis" in window;
    setSupported(audioOk || speechOk);
    if (!speechOk) return;
    const pickVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length === 0) return;
      const en = voices.filter((v) => /^en[-_]/i.test(v.lang) || v.lang === "en");
      voiceRef.current =
        en.find((v) => v.default) ||
        en.find((v) => /Google|Microsoft|Apple|Samantha|Daniel/i.test(v.name)) ||
        en[0] ||
        voices[0];
    };
    pickVoice();
    window.speechSynthesis.addEventListener("voiceschanged", pickVoice);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", pickVoice);
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }
    setSpeaking(false);
    setEngine(null);
  }, []);

  // Browser-speech fallback — chunked utterances, robust voice selection.
  const speakBrowser = useCallback((text: string) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      setError("Voice playback is not supported in this browser.");
      return;
    }
    setEngine("browser");
    try {
      window.speechSynthesis.cancel();
      window.speechSynthesis.resume();
    } catch {
      /* ignore */
    }
    const chunks = text
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (chunks.length === 0) {
      setError("Nothing to say.");
      return;
    }
    const utterances = chunks.map((chunk) => {
      const u = new SpeechSynthesisUtterance(chunk);
      u.rate = 1;
      u.pitch = 1;
      u.volume = 1;
      if (voiceRef.current) {
        u.voice = voiceRef.current;
        u.lang = voiceRef.current.lang;
      } else {
        u.lang = "en-US";
      }
      return u;
    });
    utterances[0].onstart = () => setSpeaking(true);
    utterances[utterances.length - 1].onend = () => {
      setSpeaking(false);
      setEngine(null);
    };
    for (const u of utterances) {
      u.onerror = (ev) => {
        const err = (ev as SpeechSynthesisErrorEvent).error;
        if (err && err !== "canceled" && err !== "interrupted") {
          setError(`Speech error: ${err}`);
        }
        setSpeaking(false);
        setEngine(null);
      };
    }
    Promise.resolve().then(() => {
      for (const u of utterances) {
        try {
          window.speechSynthesis.speak(u);
        } catch (e) {
          setError((e as Error).message);
        }
      }
    });
  }, []);

  /**
   * Speak a server-built briefing. Calls /api/voice/briefing for streamed
   * Camb.ai audio. Falls back to the browser engine on any failure.
   */
  const speakBriefing = useCallback(
    async (params: { lat: number; lon: number; location: string }) => {
      if (speaking) {
        stop();
        return;
      }
      setError(null);
      setEngine("camb");
      const url = `/api/voice/briefing?lat=${encodeURIComponent(params.lat)}&lon=${encodeURIComponent(params.lon)}&location=${encodeURIComponent(params.location)}&format=mp3`;
      try {
        const res = await fetch(url, { method: "GET" });
        if (!res.ok) {
          // Try to extract a fallback briefing text from the same endpoint.
          let fallbackText: string | null = null;
          try {
            const tRes = await fetch(`${url}&text=1`);
            if (tRes.ok) {
              const json = (await tRes.json()) as { text?: string };
              fallbackText = json.text ?? null;
            }
          } catch {
            /* ignore */
          }
          if (fallbackText) {
            speakBrowser(fallbackText);
            return;
          }
          // Give caller a chance to provide local fallback text.
          throw new Error(`Voice service ${res.status}`);
        }
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const audio = new Audio(objectUrl);
        audioRef.current = audio;
        audio.onplay = () => setSpeaking(true);
        audio.onended = () => {
          setSpeaking(false);
          setEngine(null);
          URL.revokeObjectURL(objectUrl);
        };
        audio.onerror = () => {
          setSpeaking(false);
          setEngine(null);
          URL.revokeObjectURL(objectUrl);
          setError("Audio playback failed.");
        };
        await audio.play();
      } catch (e) {
        // Fall back to local Web Speech with a generic text builder upstream.
        setEngine(null);
        throw e;
      }
    },
    [speaking, stop, speakBrowser],
  );

  // For local fallback when caller already has a text string.
  const speakText = useCallback((text: string) => {
    if (speaking) {
      stop();
      return;
    }
    speakBrowser(text);
  }, [speaking, stop, speakBrowser]);

  return { speakBriefing, speakText, stop, speaking, supported, error, engine };
}
